// =============================================================================
// US-BUCKET-02 -- Accept or change the default template before first use
// US-BUCKET-17 -- Set initial bucket balances at new-child account setup
// =============================================================================
//
// Shown immediately after a guardian creates a new child (US-SETUP-02).
// Lists the child's default buckets (Spend / Save / Tithe per the
// US-BUCKET-01 template) and lets the guardian:
//   * Rename each bucket
//   * Change each bucket's allocation percent
//   * Remove a bucket entirely (deactivate)
//   * Set a starting balance for each bucket (US-BUCKET-17) -- recorded as
//     an 'initial_balance' adjustment, not a deposit
//   * Skip the review and accept the defaults as-is
//
// Edits are sent to the backend via the existing bucket_update /
// bucket_deactivate commands. Initial balances use the new
// bucket_balance_adjust command with source_code='initial_balance'.

import {
  type AppError,
  type BucketEntry,
  bucketBalanceAdjust,
  bucketDeactivate,
  bucketUpdate,
  bucketUpdateSplits,
  bucketsList,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  currency: string;
  onDone: () => void;
};

function bpsToPercentString(bps: number): string {
  return (bps / 100).toFixed(2);
}

function percentStringToBps(s: string): number | null {
  const n = Number.parseFloat(s);
  if (Number.isNaN(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100);
}

function parseBalance(s: string): number | null {
  const clean = s.replace(/[^0-9.]/g, '');
  if (!clean) return 0; // empty = zero, not an error
  const f = Number.parseFloat(clean);
  if (Number.isNaN(f) || f < 0) return null;
  return Math.round(f * 100);
}

export const ReviewBucketsScreen: Component<Props> = (props) => {
  const [buckets, { refetch }] = createResource(async () => {
    const r = await bucketsList({ sessionId: props.sessionId });
    if (!r.ok) return [] as BucketEntry[];
    return r.value.filter((b) => b.childAccountId === props.childAccountId);
  });

  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const [submittingBucketId, setSubmittingBucketId] = createSignal<number | null>(null);
  const [savingBalances, setSavingBalances] = createSignal(false);

  const [nameDrafts, setNameDrafts] = createSignal<Record<number, string>>({});
  const [percentDrafts, setPercentDrafts] = createSignal<Record<number, string>>({});
  // US-BUCKET-17: starting balance drafts (display-currency decimal strings)
  const [balanceDrafts, setBalanceDrafts] = createSignal<Record<number, string>>({});

  function handleErr(e: AppError | string): string {
    if (typeof e === 'string') return e;
    return e.message ?? 'Something went wrong. Please try again.';
  }

  async function commitRename(bucket: BucketEntry) {
    const draft = nameDrafts()[bucket.bucketId];
    if (draft === undefined || draft === bucket.bucketName) return;
    if (draft.trim() === '') {
      setErrorMessage('Bucket name cannot be empty.');
      return;
    }
    setSubmittingBucketId(bucket.bucketId);
    setErrorMessage(null);
    const r = await bucketUpdate({
      sessionId: props.sessionId,
      bucketId: bucket.bucketId,
      bucketName: draft.trim(),
    });
    setSubmittingBucketId(null);
    if (!r.ok) {
      setErrorMessage(handleErr(r.error));
      return;
    }
    setNameDrafts((prev) => {
      const next = { ...prev };
      delete next[bucket.bucketId];
      return next;
    });
    await refetch();
  }

  /**
   * Splits must always add up to 100%.  Keeping the edits as a single draft
   * prevents the old one-field-at-a-time update from sending an invalid
   * intermediate total (for example, 6000 bps) to the backend.
   */
  async function saveAllocationDrafts(): Promise<boolean> {
    const list = buckets() ?? [];
    const splits = list.map((bucket) => {
      const draft = percentDrafts()[bucket.bucketId];
      return {
        bucketId: bucket.bucketId,
        allocationBps: draft === undefined ? bucket.allocationBps : percentStringToBps(draft),
      };
    });
    if (splits.some((split) => split.allocationBps === null)) {
      setErrorMessage('Each split must be a number between 0% and 100%.');
      return false;
    }
    const totalBps = splits.reduce((sum, split) => sum + (split.allocationBps ?? 0), 0);
    if (totalBps !== 10_000) {
      setErrorMessage(
        `Your splits add up to ${(totalBps / 100).toFixed(2)}%. They need to add up to 100%.`,
      );
      return false;
    }
    const hasChanges = splits.some(
      (split, index) => split.allocationBps !== list[index]?.allocationBps,
    );
    if (!hasChanges) return true;

    setErrorMessage(null);
    const result = await bucketUpdateSplits(
      props.sessionId,
      props.childAccountId,
      splits.map((split) => ({
        bucketId: split.bucketId,
        allocationBps: split.allocationBps ?? 0,
      })),
    );
    if (!result.ok) {
      setErrorMessage(handleErr(result.error));
      return false;
    }
    setPercentDrafts({});
    await refetch();
    return true;
  }

  function applyPreset(values: readonly number[]) {
    const list = buckets() ?? [];
    if (list.length !== values.length) return;
    setPercentDrafts(
      Object.fromEntries(
        list.map((bucket, index) => [bucket.bucketId, String(values[index] ?? 0)]),
      ),
    );
    setErrorMessage(null);
  }

  async function handleRemove(bucket: BucketEntry) {
    setErrorMessage(null);
    const active = buckets() ?? [];
    if (active.length <= 1) {
      setErrorMessage('You must keep at least one active bucket.');
      return;
    }
    if (
      !confirm(
        `Remove the "${bucket.bucketName}" bucket? Its allocation will return to the flexible pool.`,
      )
    )
      return;
    setSubmittingBucketId(bucket.bucketId);
    const r = await bucketDeactivate({
      sessionId: props.sessionId,
      bucketId: bucket.bucketId,
      targetBucketId: null,
    });
    setSubmittingBucketId(null);
    if (!r.ok) {
      setErrorMessage(handleErr(r.error));
      return;
    }
    await refetch();
  }

  async function commitStartingBalances() {
    // Only commit non-zero balance drafts.
    const list = buckets() ?? [];
    const toSave = list.filter((b) => {
      const raw = balanceDrafts()[b.bucketId];
      if (!raw) return false;
      const minor = parseBalance(raw);
      return minor !== null && minor !== 0;
    });

    setSavingBalances(true);
    setErrorMessage(null);
    if (!(await saveAllocationDrafts())) {
      setSavingBalances(false);
      return;
    }

    for (const bucket of toSave) {
      const raw = balanceDrafts()[bucket.bucketId] ?? '';
      const minor = parseBalance(raw);
      if (minor === null || minor === 0) continue;
      const r = await bucketBalanceAdjust({
        sessionId: props.sessionId,
        bucketId: bucket.bucketId,
        newBalanceMinorUnits: minor,
        reason: 'Initial account setup',
        sourceCode: 'initial_balance',
      });
      if (!r.ok) {
        setErrorMessage(`Failed to set ${bucket.bucketName} balance: ${handleErr(r.error)}`);
        setSavingBalances(false);
        return;
      }
    }
    setSavingBalances(false);
    props.onDone();
  }

  return (
    <ActionScreen
      class="action-screen--bucket-review"
      eyebrow={`Starting plan for ${props.childDisplayName}`}
      title="Bucket split"
      onBack={props.onDone}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          onClick={() => void commitStartingBalances()}
          disabled={savingBalances()}
          data-testid="review-buckets-save-continue"
        >
          {savingBalances() ? 'Saving…' : 'Save & continue'}
        </button>
      }
    >
      <p class="action-screen-intro">
        These are the default buckets for {props.childDisplayName}. Rename, change the allocation
        percent, or remove any bucket. You can also enter starting balances if{' '}
        {props.childDisplayName} already has real money to track -- or leave them at zero and add
        money later.
      </p>

      <Show when={(buckets() ?? []).length === 3}>
        <section class="bucket-split-presets" aria-label="Starting bucket split presets">
          <span class="action-screen-eyebrow">Quick split</span>
          <button
            type="button"
            class="action-chip"
            onClick={() => applyPreset([80, 10, 10])}
            data-testid="review-buckets-preset-80-10-10"
          >
            80 / 10 / 10
          </button>
          <button
            type="button"
            class="action-chip"
            onClick={() => applyPreset([50, 30, 20])}
            data-testid="review-buckets-preset-50-30-20"
          >
            50 / 30 / 20
          </button>
          <button
            type="button"
            class="action-chip"
            onClick={() => applyPreset([60, 30, 10])}
            data-testid="review-buckets-preset-60-30-10"
          >
            60 / 30 / 10
          </button>
        </section>
      </Show>

      <Show when={errorMessage()}>
        {(msg) => (
          <div class="form-error" role="alert" data-testid="review-buckets-error">
            {msg()}
          </div>
        )}
      </Show>

      <Show when={buckets()} fallback={<p>Loading buckets…</p>}>
        {(list) => (
          <section class="bucket-review-list" data-testid="review-buckets-list">
            <For each={list()}>
              {(bucket) => {
                const draftName = () => nameDrafts()[bucket.bucketId] ?? bucket.bucketName;
                const draftPercent = () =>
                  percentDrafts()[bucket.bucketId] ?? bpsToPercentString(bucket.allocationBps);
                const draftBalance = () => balanceDrafts()[bucket.bucketId] ?? '';
                const isSubmitting = () => submittingBucketId() === bucket.bucketId;
                return (
                  <div class="bucket-review-card" data-testid={`review-bucket-${bucket.bucketId}`}>
                    <div class="bucket-review-card-head">
                      <input
                        type="text"
                        aria-label={`Bucket name for ${bucket.bucketName}`}
                        value={draftName()}
                        disabled={isSubmitting()}
                        onInput={(e) =>
                          setNameDrafts((p) => ({
                            ...p,
                            [bucket.bucketId]: e.currentTarget.value,
                          }))
                        }
                        onBlur={() => void commitRename(bucket)}
                        data-testid={`review-bucket-name-${bucket.bucketId}`}
                        class="bucket-review-name"
                      />
                      <button
                        type="button"
                        class="bucket-review-remove"
                        disabled={isSubmitting()}
                        onClick={() => void handleRemove(bucket)}
                        data-testid={`review-bucket-remove-${bucket.bucketId}`}
                        aria-label={`Remove ${bucket.bucketName}`}
                      >
                        Remove
                      </button>
                    </div>
                    <div class="bucket-review-fields">
                      <label>
                        <span>Split</span>
                        <div class="review-bucket-percent">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            aria-label={`Allocation percent for ${bucket.bucketName}`}
                            value={draftPercent()}
                            disabled={isSubmitting()}
                            onInput={(e) =>
                              setPercentDrafts((p) => ({
                                ...p,
                                [bucket.bucketId]: e.currentTarget.value,
                              }))
                            }
                            data-testid={`review-bucket-percent-${bucket.bucketId}`}
                            class="bucket-review-input"
                          />
                          <span aria-hidden="true">%</span>
                        </div>
                      </label>
                      <label>
                        <span>Starting balance</span>
                        <div class="review-bucket-balance">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label={`Starting balance for ${bucket.bucketName}`}
                            value={draftBalance()}
                            placeholder="0.00"
                            onInput={(e) =>
                              setBalanceDrafts((p) => ({
                                ...p,
                                [bucket.bucketId]: e.currentTarget.value,
                              }))
                            }
                            data-testid={`review-bucket-balance-${bucket.bucketId}`}
                            class="bucket-review-input"
                          />
                        </div>
                      </label>
                    </div>
                  </div>
                );
              }}
            </For>
          </section>
        )}
      </Show>

      <button
        type="button"
        class="ghost"
        onClick={() => props.onDone()}
        data-testid="review-buckets-skip"
      >
        Use these defaults
      </button>
    </ActionScreen>
  );
};
